import Script from 'next/script'
import * as React from 'react'
import { useFeatureValueLoadable } from 'growthbook'
import { useUserLocation } from 'hooks/use-user-location'

import {
  createIubendaBridgeScript,
  iubendaConsentEventName,
  IUBENDA_FEATURE_FLAG,
  IubendaStyles,
  iubendaSrc,
  isIubendaBridgeReady,
  useIubenda,
} from '@repo/analytics/iubenda'
import type { IubendaConsentEventDetail } from '@repo/analytics/iubenda'

const isBrowserTest = () => process.env.TEST_BROWSER === '1'

export type IubendaConfig = ReturnType<typeof useIubendaConfig>

export const useIubendaConfig = () => {
  const iubendaFeature = useFeatureValueLoadable<boolean>(IUBENDA_FEATURE_FLAG)
  const userLocation = useUserLocation()
  const iubendaConfig = useIubenda({
    country: userLocation.country,
    enabled: !isBrowserTest() && iubendaFeature.data === true,
    locationSet: userLocation.locationSet,
    region: userLocation.region,
  })

  if (iubendaFeature.isLoading) {
    return {
      ...iubendaConfig,
      scriptProps: {},
      variant: 'locationNotSet' as const,
    }
  }

  return iubendaConfig
}

export const IubendaScripts = () => {
  const { variant } = useIubendaConfig()
  const [bridgeReadyVariant, setBridgeReadyVariant] = React.useState<string | null>(null)

  const markBridgeReady = React.useCallback(() => {
    setBridgeReadyVariant(variant)
  }, [variant])

  React.useEffect(() => {
    const handleConsentEvent = (event: Event) => {
      const detail = (event as CustomEvent<Partial<IubendaConsentEventDetail>>).detail

      if (detail?.type === 'bridge-ready') {
        markBridgeReady()
      }
    }

    window.addEventListener(iubendaConsentEventName, handleConsentEvent)

    if (isIubendaBridgeReady()) {
      markBridgeReady()
    }

    return () => {
      window.removeEventListener(iubendaConsentEventName, handleConsentEvent)
    }
  }, [markBridgeReady])

  if (variant === 'locationNotSet' || variant === 'notApplicable') {
    return null
  }

  const bridgeReady = bridgeReadyVariant === variant
  const autoOpenPreferences = variant === 'strictMode'

  return (
    <>
      <IubendaStyles />
      <Script
        id="iubenda-react-bridge-script"
        strategy="afterInteractive"
        onReady={markBridgeReady}
      >
        {createIubendaBridgeScript({ autoOpenPreferences })}
      </Script>
      {bridgeReady ? (
        <Script
          type="text/javascript"
          id="iubenda-script"
          strategy="afterInteractive"
          src={iubendaSrc}
        />
      ) : null}
    </>
  )
}
